import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { SetupChargeApiError, SetupChargeService } from "../../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupCharge, ISetupChargeErrorDetail, TSetupChargeAction } from "../../../../shared/services/api/setupCharge/Types";
import { globalMaskReal } from "../../../../shared/utils";
import { setupChargeDateToIso, setupChargeFormatCurrency, setupChargeToday, setupChargeToNumber } from "../../utils/setupChargeFormat";
import { isValidPhone, onlyDigits } from "../../utils/setupChargeValidators";

interface ModalSetupChargeActionProps {
	/** `null` mantém o modal fechado. */
	action: TSetupChargeAction | null;
	charge: ISetupCharge | null;
	onClose: () => void;
	/** Chamado com a cobrança já atualizada pela API, para a tela refletir o novo estado. */
	onDone: (action: TSetupChargeAction, updated: ISetupCharge) => void;
}

const TITLES: Record<TSetupChargeAction, string> = {
	cancel: "Cancelar taxa avulsa",
	resend: "Reenviar link de pagamento",
	contact_update: "Corrigir contato do pagador",
	manual_payment: "Dar baixa manual",
	repass: "Registrar repasse",
	reprocess: "Reprocessar taxa avulsa",
};

const CONFIRM_LABEL: Record<TSetupChargeAction, string> = {
	cancel: "Cancelar taxa",
	resend: "Reenviar link",
	contact_update: "Salvar contato",
	manual_payment: "Confirmar baixa",
	repass: "Registrar repasse",
	reprocess: "Reprocessar",
};

const CONFIRM_TEXT: Partial<Record<TSetupChargeAction, string>> = {
	resend: "O link de pagamento atual será enviado novamente para o e-mail do pagador.",
	reprocess: "A cobrança será enviada de novo ao gateway, sem refazer o que já foi concluído.",
};

const parseMoney = (text: string): number | undefined => setupChargeToNumber(text.replace(/\./g, "").replace(",", "."));

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ModalSetupChargeAction = ({ action, charge, onClose, onDone }: ModalSetupChargeActionProps) => {
	const [reason, setReason] = useState("");
	const [note, setNote] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [paymentDate, setPaymentDate] = useState(setupChargeToday());
	const [amount, setAmount] = useState("");
	const [repassDate, setRepassDate] = useState(setupChargeToday());
	const [justification, setJustification] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [apiError, setApiError] = useState<string | null>(null);

	useEffect(() => {
		if (!action || !charge) return;
		setReason("");
		setNote("");
		setJustification("");
		setEmail(charge.payerEmail ?? "");
		setPhone(charge.payerPhone ?? "");
		setPaymentDate(setupChargeToday());
		setRepassDate(setupChargeToday());
		const expected = setupChargeToNumber(charge.expectedRepass);
		setAmount(expected !== undefined ? globalMaskReal(expected.toFixed(2)) : "");
		setFieldErrors({});
		setApiError(null);
		setIsSending(false);
	}, [action, charge?.id]);

	if (!action || !charge) return null;

	const expected = setupChargeToNumber(charge.expectedRepass);
	const typedAmount = parseMoney(amount);
	const differsFromExpected = action === "repass" && typedAmount !== undefined && expected !== undefined && Math.abs(typedAmount - expected) > 0.005;

	const validate = (): Record<string, string> => {
		const errors: Record<string, string> = {};
		if (action === "cancel" && !reason.trim()) errors.reason = "Informe o motivo do cancelamento.";
		if (action === "contact_update") {
			if (!EMAIL_PATTERN.test(email.trim())) errors.email = "Informe um e-mail válido.";
			if (!isValidPhone(phone)) errors.phone = "Informe o celular com DDD.";
		}
		if (action === "manual_payment") {
			if (!paymentDate) errors.paymentDate = "Informe a data do pagamento.";
			else if (paymentDate > setupChargeToday()) errors.paymentDate = "A data do pagamento não pode ser futura.";
			if (!reason.trim()) errors.reason = "Informe o motivo da baixa manual.";
		}
		if (action === "repass") {
			if (typedAmount === undefined || typedAmount <= 0) errors.amount = "Informe o valor repassado, maior que zero.";
			if (!repassDate) errors.repassDate = "Informe a data do repasse.";
			if (differsFromExpected && !justification.trim()) errors.justification = "O valor é diferente do esperado: informe a justificativa.";
		}
		return errors;
	};

	const applyApiError = (error: SetupChargeApiError) => {
		const byField: Record<string, string> = {};
		error.details.forEach((detail: ISetupChargeErrorDetail) => {
			if (detail.field) byField[detail.field] = detail.message ?? detail.reason ?? "";
		});
		setFieldErrors(byField);
		setApiError(error.message);
	};

	const handleConfirm = async () => {
		const errors = validate();
		setFieldErrors(errors);
		setApiError(null);
		if (Object.keys(errors).length > 0) return;

		setIsSending(true);
		let result;
		switch (action) {
			case "cancel":
				result = await SetupChargeService.cancel(charge.id, { reason: reason.trim(), note: note.trim() || undefined });
				break;
			case "resend":
				result = await SetupChargeService.resend(charge.id);
				break;
			case "contact_update":
				result = await SetupChargeService.updateContact(charge.id, { email: email.trim(), phone: onlyDigits(phone) });
				break;
			case "manual_payment":
				result = await SetupChargeService.manualPayment(charge.id, { paymentDate: setupChargeDateToIso(paymentDate), reason: reason.trim(), note: note.trim() || undefined });
				break;
			case "repass":
				result = await SetupChargeService.repass(charge.id, {
					amount: typedAmount as number,
					repassDate: setupChargeDateToIso(repassDate),
					note: note.trim() || undefined,
					justification: justification.trim() || undefined,
				});
				break;
			default:
				result = await SetupChargeService.reprocess(charge.id);
		}
		setIsSending(false);

		if (result instanceof Error) {
			applyApiError(result as SetupChargeApiError);
			return;
		}
		onDone(action, result);
	};

	return (
		<Dialog open onClose={isSending ? undefined : onClose} fullWidth maxWidth="sm">
			<DialogTitle>{TITLES[action]}</DialogTitle>
			<DialogContent dividers>
				<Box mb={2}>
					<Typography fontWeight="bold">{charge.payerName ?? ""}</Typography>
					<Typography variant="body2" color="textSecondary">
						{charge.clientName ?? ""} · {setupChargeFormatCurrency(charge.amount)}
					</Typography>
				</Box>

				{apiError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{apiError}
					</Alert>
				)}

				{CONFIRM_TEXT[action] && <Typography>{CONFIRM_TEXT[action]}</Typography>}
				{action === "manual_payment" && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						A cobrança no gateway será cancelada e a taxa passará a constar como paga. Confirme que o pagamento já foi recebido por outro meio.
					</Alert>
				)}

				<Grid container spacing={2}>
					{action === "cancel" && (
						<Grid item xs={12}>
							<TextField fullWidth required multiline minRows={2} label="Motivo do cancelamento" value={reason} onChange={(e) => setReason(e.target.value)} error={!!fieldErrors.reason} helperText={fieldErrors.reason} />
						</Grid>
					)}

					{action === "contact_update" && (
						<>
							<Grid item xs={12} sm={7}>
								<TextField fullWidth required label="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} error={!!fieldErrors.email} helperText={fieldErrors.email} />
							</Grid>
							<Grid item xs={12} sm={5}>
								<TextField fullWidth required label="Celular" value={phone} onChange={(e) => setPhone(e.target.value)} error={!!fieldErrors.phone} helperText={fieldErrors.phone} />
							</Grid>
						</>
					)}

					{action === "manual_payment" && (
						<>
							<Grid item xs={12} sm={5}>
								<TextField
									fullWidth
									required
									type="date"
									label="Data do pagamento"
									InputLabelProps={{ shrink: true }}
									inputProps={{ max: setupChargeToday() }}
									value={paymentDate}
									onChange={(e) => setPaymentDate(e.target.value)}
									error={!!fieldErrors.paymentDate}
									helperText={fieldErrors.paymentDate}
								/>
							</Grid>
							<Grid item xs={12}>
								<TextField fullWidth required multiline minRows={2} label="Motivo da baixa manual" value={reason} onChange={(e) => setReason(e.target.value)} error={!!fieldErrors.reason} helperText={fieldErrors.reason} />
							</Grid>
						</>
					)}

					{action === "repass" && (
						<>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									required
									label="Valor repassado (R$)"
									value={amount}
									onChange={(e) => setAmount(globalMaskReal(e.target.value))}
									error={!!fieldErrors.amount}
									helperText={fieldErrors.amount ?? (expected !== undefined ? `Esperado: ${setupChargeFormatCurrency(expected)}` : undefined)}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									required
									type="date"
									label="Data do repasse"
									InputLabelProps={{ shrink: true }}
									value={repassDate}
									onChange={(e) => setRepassDate(e.target.value)}
									error={!!fieldErrors.repassDate}
									helperText={fieldErrors.repassDate}
								/>
							</Grid>
							{differsFromExpected && (
								<Grid item xs={12}>
									<TextField
										fullWidth
										required
										multiline
										minRows={2}
										label="Justificativa da diferença"
										value={justification}
										onChange={(e) => setJustification(e.target.value)}
										error={!!fieldErrors.justification}
										helperText={fieldErrors.justification}
									/>
								</Grid>
							)}
						</>
					)}

					{(action === "cancel" || action === "manual_payment" || action === "repass") && (
						<Grid item xs={12}>
							<TextField fullWidth multiline minRows={2} label="Observação (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isSending}>
					Voltar
				</Button>
				<Button variant="contained" disableElevation color={action === "cancel" ? "error" : "primary"} onClick={handleConfirm} disabled={isSending} startIcon={isSending ? <CircularProgress size={16} /> : undefined}>
					{CONFIRM_LABEL[action]}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
